/**
 * Collection grid: filtering, sorting, Load More and grid density.
 *
 * Every control is a real link or a real form first, so the page works with
 * this script missing. With it, the section is swapped in place through the
 * Section Rendering API and the address bar follows, so back, forward and
 * refresh all land where the visitor was. The header's Refine row is swapped
 * with it, since both show which tags are applied.
 *
 * Tag groups (Colour, Fabric and the rest) filter through Shopify's tag URLs,
 * /collections/leggings/colour-black+fabric-youmove, so the drawer puts the
 * chosen tags in the path and everything else in the query string. The
 * search page (sections/nyl-search.liquid) is built from the same pieces;
 * there the tags go in the search itself.
 *
 * Listeners are delegated from the document because the section element is
 * replaced on every change.
 */
(() => {
  const SECTION = '[data-nyl-collection]';
  const DENSITY_KEY = 'nyl:collection:density';

  const section = () => document.querySelector(SECTION);

  const sameOrigin = (url) => {
    try {
      return new URL(url, window.location.href).origin === window.location.origin;
    } catch (error) {
      return false;
    }
  };

  const withSection = (url, id) => {
    const target = new URL(url, window.location.href);
    target.searchParams.set('section_id', id);
    return target.toString();
  };

  const parse = (html) => (html ? new DOMParser().parseFromString(html, 'text/html') : null);

  // Several sections in one request, keyed by section id.
  const fetchSections = async (url, ids, signal) => {
    if (!ids.length || !sameOrigin(url)) throw new Error('unrenderable');
    const target = new URL(url, window.location.href);
    target.searchParams.set('sections', ids.join(','));
    // No Accept: application/json here. Shopify answers that with the
    // collection's own JSON rather than the rendered sections.
    const response = await fetch(target.toString(), { signal });
    if (!response.ok) throw new Error(String(response.status));
    const payload = await response.json();
    return ids.map((id) => parse(payload?.[id]));
  };

  const fetchSection = async (url, signal) => {
    const id = section()?.dataset.sectionId;
    if (!id || !sameOrigin(url)) throw new Error('unrenderable');
    const response = await fetch(withSection(url, id), { headers: { Accept: 'text/html' }, signal });
    if (!response.ok) throw new Error(String(response.status));
    const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
    const next = doc.querySelector(SECTION);
    if (!next) throw new Error('empty');
    return next;
  };

  /* Density ------------------------------------------------------------- */

  const readDensity = () => {
    try {
      return window.localStorage.getItem(DENSITY_KEY) === 'dense' ? 'dense' : 'standard';
    } catch (error) {
      return 'standard';
    }
  };

  const applyDensity = (root, value) => {
    if (!root) return;
    root.classList.toggle('is-dense', value === 'dense');
    root.querySelectorAll('[data-nyl-density]').forEach((button) => {
      const on = button.dataset.nylDensity === value;
      button.classList.toggle('is-current', on);
      button.setAttribute('aria-pressed', String(on));
    });
  };

  /* Rendering ----------------------------------------------------------- */

  let inflight = null;
  let inflightUrl = '';
  let inflightChip = '';
  let queuedChip = null;

  const announce = () => {
    // Injected cards carry hearts and swatches that read their own state.
    document.dispatchEvent(new CustomEvent('nyl:content', { bubbles: true }));
  };

  /* Refine chips -------------------------------------------------------- */

  const chipsRow = (refine) => refine?.querySelector('.nyl-collection-header__chips');

  // The new row picks up the old row's swipe, then moves only as far as it
  // takes to show the chip that was chosen, whole.
  const keepChipsInView = (refine, scrollLeft, handle) => {
    const row = chipsRow(refine);
    if (!row) return;
    row.scrollLeft = scrollLeft;
    if (!handle) return;
    const chip = row.querySelector(`[data-nyl-chip="${CSS.escape(handle)}"]`);
    if (!chip) return;
    const rowBox = row.getBoundingClientRect();
    const chipBox = chip.getBoundingClientRect();
    const end = rowBox.right - parseFloat(getComputedStyle(row).paddingInlineEnd || 0);
    if (chipBox.left < rowBox.left) row.scrollLeft -= rowBox.left - chipBox.left;
    else if (chipBox.right > end) row.scrollLeft += chipBox.right - end;
  };

  // The chip shows its new state the moment it is tapped, while the grid is
  // fetched, so a tap never looks like it missed. The row that arrives with
  // the grid replaces this guess with what Shopify says.
  const markChip = (link) => {
    const refine = link.closest('[data-nyl-refine]');
    if (!refine) return;
    const chips = [...refine.querySelectorAll('[data-nyl-chip]')];
    const all = chips.find((chip) => chip.dataset.nylChip === 'all');
    const set = (chip, on) => {
      chip.classList.toggle('is-current', on);
      if (on) chip.setAttribute('aria-current', 'true');
      else chip.removeAttribute('aria-current');
    };

    if (link === all) {
      chips.forEach((chip) => set(chip, chip === all));
    } else if (link.classList.contains('is-current')) {
      set(link, false);
      if (all && !chips.some((chip) => chip !== all && chip.classList.contains('is-current'))) set(all, true);
    } else {
      chips.forEach((chip) => {
        if (chip !== link && chip.dataset.nylChipGroup === link.dataset.nylChipGroup) set(chip, false);
      });
      set(link, true);
      if (all) set(all, false);
    }
    refine.setAttribute('aria-busy', 'true');
  };

  const render = async (url, { push = true, chip = '' } = {}) => {
    const root = section();
    if (!root) return;

    // A second tap on something already on its way changes nothing, so it
    // leaves that request running rather than starting it over.
    const target = new URL(url, window.location.href).toString();
    if (inflight && inflightUrl === target) return;

    inflight?.abort();
    const controller = new AbortController();
    inflight = controller;
    inflightUrl = target;
    inflightChip = chip;
    if (!chip) queuedChip = null;

    root.querySelector('[data-nyl-filter-drawer]')?.close();
    root.querySelector('[data-nyl-collection-grid]')?.classList.add('is-loading');

    const header = document.querySelector('[data-nyl-collection-header]');
    const ids = [root.dataset.sectionId, header?.dataset.sectionId].filter(Boolean);

    // Where focus was, so it can land on the same control once replaced.
    const focused = document.activeElement;
    const focusedChip = focused?.closest?.('[data-nyl-chip]')?.dataset.nylChip;
    const focusedInGrid = !focusedChip && root.contains(focused);

    // How far the Refine row was swiped, so the new row opens at the same
    // place instead of back at All with the chosen chip out of sight.
    const chipsScroll = document.querySelector('[data-nyl-refine] .nyl-collection-header__chips')?.scrollLeft || 0;

    try {
      const [gridDoc, headerDoc] = await fetchSections(url, ids, controller.signal);
      const next = gridDoc?.querySelector(SECTION);
      if (!next) throw new Error('empty');
      applyDensity(next, readDensity());
      root.replaceWith(next);

      const refine = document.querySelector('[data-nyl-refine]');
      const nextRefine = headerDoc?.querySelector('[data-nyl-refine]');
      if (refine && nextRefine) {
        refine.replaceWith(nextRefine);
        keepChipsInView(nextRefine, chipsScroll, chip);
      } else if (refine && next.contains(refine)) {
        // The search page keeps its Refine row in the section itself.
        keepChipsInView(refine, chipsScroll, chip);
      }

      if (push) window.history.pushState({ nylCollection: true }, '', url);
      if (next.dataset.nylPageTitle) document.title = next.dataset.nylPageTitle;
      announce();

      if (focusedChip) {
        document.querySelector(`[data-nyl-chip="${CSS.escape(focusedChip)}"]`)?.focus({ preventScroll: true });
      } else if (focusedInGrid) {
        next.querySelector('[data-nyl-filter-open]')?.focus({ preventScroll: true });
      }

      // Keep the toolbar in view rather than leaving the visitor mid grid.
      const top = next.getBoundingClientRect().top;
      if (top < 0) next.scrollIntoView({ block: 'start' });
    } catch (error) {
      if (error.name === 'AbortError') return;
      // Anything unexpected falls back to the page the link or form describes.
      window.location.assign(url);
      return;
    } finally {
      if (inflight === controller) {
        inflight = null;
        inflightUrl = '';
        inflightChip = '';
      }
    }

    runQueuedChip();
  };

  // A chip tapped while another is on its way waits for the row that one
  // brings back, since only that row's links carry both choices.
  const chooseChip = (link) => {
    const handle = link.dataset.nylChip || '';
    if (inflight && inflightChip) {
      // Tapping the chip already on its way again asks for the same thing.
      if (handle === inflightChip) return;
      markChip(link);
      queuedChip = { handle, on: link.classList.contains('is-current') };
      return;
    }
    markChip(link);
    render(link.href, { chip: handle });
  };

  function runQueuedChip() {
    if (!queuedChip || inflight) return;
    const { handle, on } = queuedChip;
    queuedChip = null;
    const link = document.querySelector(`[data-nyl-refine] [data-nyl-chip="${CSS.escape(handle)}"]`);
    // Already where the tap asked for, or gone from the row: nothing to do.
    if (!link || link.classList.contains('is-current') === on) return;
    chooseChip(link);
  }

  const loadMore = async (link) => {
    const root = section();
    const grid = root?.querySelector('[data-nyl-collection-grid]');
    if (!grid) return;

    link.classList.add('is-loading');
    try {
      const next = await fetchSection(link.href);
      const arrivals = next.querySelectorAll('[data-nyl-collection-grid] > .nyl-collection__item');
      const first = arrivals[0];
      grid.append(...arrivals);
      const more = next.querySelector('[data-nyl-collection-more]');
      root.querySelector('[data-nyl-collection-more]')?.replaceWith(more);
      announce();
      // Keyboard users continue from the first new product, not the top.
      first?.querySelector('a[href]')?.focus({ preventScroll: true });
    } catch (error) {
      window.location.assign(link.href);
    }
  };

  /* Controls ------------------------------------------------------------ */

  const plainClick = (event) =>
    event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;

  document.addEventListener('click', (event) => {
    if (!section()) return;

    const opener = event.target.closest('[data-nyl-filter-open]');
    if (opener) {
      const dialog = section().querySelector('[data-nyl-filter-drawer]');
      if (!dialog) return;
      const wanted = opener.dataset.nylFilterOpen;
      let group = null;
      if (wanted) {
        group = dialog.querySelector(`[data-nyl-filter-group="${CSS.escape(wanted)}"]`);
        if (group) group.open = true;
      }
      dialog.showModal();
      group?.scrollIntoView({ block: 'nearest' });
      return;
    }

    const closer = event.target.closest('[data-nyl-filter-close]');
    if (closer) {
      closer.closest('dialog')?.close();
      return;
    }

    // A click that lands on the dialog itself is on the backdrop.
    if (event.target.matches?.('[data-nyl-filter-drawer]')) {
      event.target.close();
      return;
    }

    const density = event.target.closest('[data-nyl-density]');
    if (density) {
      const value = density.dataset.nylDensity === 'dense' ? 'dense' : 'standard';
      try {
        window.localStorage.setItem(DENSITY_KEY, value);
      } catch (error) {
        // Private browsing. The choice still applies to this page.
      }
      applyDensity(section(), value);
      return;
    }

    const more = event.target.closest('[data-nyl-load-more]');
    if (more && plainClick(event)) {
      event.preventDefault();
      loadMore(more);
      return;
    }

    const link = event.target.closest('[data-nyl-filter-link]');
    if (link && plainClick(event) && sameOrigin(link.href)) {
      event.preventDefault();
      if (link.dataset.nylChip) chooseChip(link);
      else render(link.href);
    }
  });

  document.addEventListener('submit', (event) => {
    const form = event.target.closest('[data-nyl-filter-form]');
    if (!form || !section()) return;
    event.preventDefault();

    // A search has no tag addresses, so chosen tags join the search itself,
    // tag:"Length: 7/8 Length", which Shopify's search reads as a filter,
    // after the words typed and any tag the drawer does not own.
    if (form.dataset.nylSearchTerms !== undefined) {
      const tags = new Set((form.dataset.nylKeepTags || '').split('|').filter(Boolean));
      form.querySelectorAll('[data-nyl-tag]:checked').forEach((input) => {
        if (input.dataset.nylTag) tags.add(input.dataset.nylTag);
      });
      const words = [
        form.dataset.nylSearchTerms.trim(),
        ...[...tags].sort().map((tag) => `tag:"${tag.replace(/"/g, '')}"`),
      ].filter(Boolean);

      const url = new URL(form.getAttribute('action'), window.location.href);
      for (const [key, value] of new FormData(form).entries()) {
        if (key !== 'q' && String(value).trim() !== '') url.searchParams.append(key, value);
      }
      url.searchParams.set('q', words.join(' '));
      render(url.toString());
      return;
    }

    // Chosen tags go in the path, sorted so one set of filters always has
    // one address, alongside any tag the drawer does not own.
    const tags = new Set((form.dataset.nylKeepTags || '').split('+').filter(Boolean));
    form.querySelectorAll('[data-nyl-tag]:checked').forEach((input) => {
      if (input.dataset.nylTag) tags.add(input.dataset.nylTag);
    });
    const path = [...tags].sort().map(encodeURIComponent).join('+');
    const base = form.dataset.nylCollectionUrl || form.getAttribute('action');

    const url = new URL(path ? `${base}/${path}` : base, window.location.href);
    for (const [key, value] of new FormData(form).entries()) {
      if (String(value).trim() !== '') url.searchParams.append(key, value);
    }
    render(url.toString());
  });

  document.addEventListener('change', (event) => {
    // A tag group holds one value: Shopify's tag filter wants every tag in
    // the address, so two from one group would match only products with both.
    const tag = event.target.closest?.('[data-nyl-tag]');
    if (tag) {
      if (tag.checked) {
        tag.closest('[data-nyl-tag-group]')?.querySelectorAll('[data-nyl-tag]').forEach((other) => {
          if (other !== tag) other.checked = false;
        });
      }
      return;
    }

    const select = event.target.closest('[data-nyl-sort]');
    if (!select || !section()) return;
    const url = new URL(window.location.href);
    url.searchParams.set('sort_by', select.value);
    url.searchParams.delete('page');
    render(url.toString());
  });

  window.addEventListener('popstate', () => {
    if (section()) render(window.location.href, { push: false });
  });

  applyDensity(section(), readDensity());
})();
